import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";

export default function Pagination({
  currentPage,
  setCurrentPage,
  data,
  numbers,
  npage,
  firstIndex,
  recordsPerPage,
  dataCount,
  setSelectAll,
  setCheckedItems,
}) {
  const prePage = () => {
    if (currentPage !== 1) setCurrentPage(currentPage - 1);
  };
  const nextPage = () => {
    if (currentPage !== npage) setCurrentPage(currentPage + 1);
  };
  const changeCpage = (id) => {
    setCurrentPage(id);
  };

  // Show only 5 page buttons around the current page to prevent overflow
  const maxPageNumbers = 10;
  const sidePageNumbers = Math.floor(maxPageNumbers / 2);
  let startPage = Math.max(1, currentPage - sidePageNumbers);
  let endPage = startPage + maxPageNumbers - 1;
  if (endPage > npage) {
    endPage = npage;
    startPage = Math.max(1, endPage - maxPageNumbers + 1);
  }

  return (
    <div className="px-4 mt-auto py-2 sm:px-6">
      <div className="flex flex-wrap items-center justify-between gap-y-2">
        {/* Showing X to Y of Z — nowrap so it never stacks */}
        <p className="text-sm text-[#7B809A] font-regular font-poppins whitespace-nowrap">
          Showing {firstIndex + 1} to{" "}
          {Math.min(firstIndex + recordsPerPage, dataCount)} of {dataCount} entries
        </p>

        {/* Pagination controls */}
        <nav className="inline-flex flex-wrap items-center gap-1" aria-label="Pagination">
          {/* Prev */}
          <div
            onClick={prePage}
            className="inline-flex items-center px-2 py-2 rounded-full border border-gray-300 text-sm font-medium text-gray-500 hover:bg-gray-50 cursor-pointer"
          >
            <span className="sr-only">Previous</span>
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
          </div>

          {/* First page + ellipsis */}
          {startPage > 1 && (
            <div
              onClick={() => changeCpage(1)}
              className="inline-flex items-center px-3 py-1.5 rounded-full border border-gray-300 text-sm text-gray-500 hover:bg-gray-50 cursor-pointer"
            >
              1
            </div>
          )}
          {startPage > 2 && (
            <div className="inline-flex items-center px-2 py-1.5 text-sm text-gray-400">
              ...
            </div>
          )}

          {/* Visible page numbers */}
          {numbers.slice(startPage - 1, endPage).map((number) => (
            <div
              key={number}
              onClick={() => changeCpage(number)}
              className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium cursor-pointer ${
                currentPage === number
                  ? "bg-gradient-to-b from-[#1190E4] to-[#3552D8] text-white"
                  : "border border-gray-300 text-gray-700 hover:from-[#3552D8] hover:to-[#1190E4] hover:bg-gradient-to-b hover:text-white"
              }`}
            >
              {number}
            </div>
          ))}

          {/* Trailing ellipsis + last page */}
          {endPage < npage - 1 && (
            <div className="inline-flex items-center px-2 py-1.5 text-sm text-gray-400">
              ...
            </div>
          )}
          {endPage < npage && (
            <div
              onClick={() => changeCpage(npage)}
              className="inline-flex items-center px-3 py-1.5 rounded-full border border-gray-300 text-sm text-gray-500 hover:bg-gray-50 cursor-pointer"
            >
              {npage}
            </div>
          )}

          {/* Next */}
          <div
            onClick={nextPage}
            className="inline-flex items-center px-2 py-2 rounded-full border border-gray-300 text-sm font-medium text-gray-500 hover:bg-gray-50 cursor-pointer"
          >
            <span className="sr-only">Next</span>
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
          </div>

          {/* ── Go-to-page ── */}
          <div className="inline-flex items-center gap-1 ml-2">
            <span className="text-sm text-gray-500 whitespace-nowrap">Go to</span>
            <input
              type="number"
              min={1}
              max={npage}
              placeholder={currentPage}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const val = parseInt(e.target.value, 10);
                  if (!isNaN(val) && val >= 1 && val <= npage) {
                    changeCpage(val);
                    e.target.value = "";
                  }
                }
              }}
              className="w-16 text-center text-sm border border-gray-300 rounded-full px-2 py-1.5 focus:outline-none focus:border-[#1190E4]"
              style={{ MozAppearance: "textfield" }}
            />
            <button
              onClick={(e) => {
                const input = e.currentTarget.previousSibling;
                const val = parseInt(input.value, 10);
                if (!isNaN(val) && val >= 1 && val <= npage) {
                  changeCpage(val);
                  input.value = "";
                }
              }}
              className="px-3 py-1.5 text-sm rounded-full bg-gradient-to-b from-[#1190E4] to-[#3552D8] text-white hover:opacity-90"
            >
              Enter ↵
            </button>
          </div>
        </nav>
      </div>
    </div>
  );
}
